"use client";

import React, { useEffect, useState } from "react";
import { EditorField } from "@/config/ecoBuilder/types";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";

interface StickyWhatsAppProps {
    id: string;
    phoneNumber?: string;
    message?: string;
    iconColor?: string;
    backgroundColor?: string;
    position?: "bottom-right" | "bottom-left";
    animation?: "none" | "pulse" | "bounce";
    enableNotification?: boolean;
    notificationMessage?: string;
    notificationDelay?: number;
    isSelected?: boolean;
}

export function StickyWhatsApp({
    id,
    phoneNumber = "",
    message = "مرحبًا، أرغب في الاستفسار عن...",
    iconColor = "#ffffff",
    backgroundColor = "#25D366",
    position = "bottom-right",
    animation = "pulse",
    enableNotification = true,
    notificationMessage = "مرحبًا 👋 هل تحتاج إلى مساعدة؟",
    notificationDelay = 5,
    isSelected
}: StickyWhatsAppProps) {
    const [showNotification, setShowNotification] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // Default icon color if not specified
    const finalIconColor = iconColor || "#ffffff";
    const finalBgColor = backgroundColor || "#25D366";

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (enableNotification && !isDismissed && notificationDelay && notificationDelay > 0) {
            timer = setTimeout(() => {
                setShowNotification(true);
            }, notificationDelay * 1000);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [enableNotification, isDismissed, notificationDelay]);

    const handleClick = () => {
        if (!phoneNumber) return;
        const cleanPhone = phoneNumber.replace(/\D/g, "");
        const encodedMsg = encodeURIComponent(message);
        window.open(`https://wa.me/${cleanPhone}?text=${encodedMsg}`, "_blank");
    };

    // Calculate position and alignment classes
    const isRight = position === "bottom-right";
    const positionClasses = isRight ? "right-6" : "left-6";
    // Fix: Align items to end (right) if position is right, start (left) if position is left
    const alignmentClasses = isRight ? "items-end" : "items-start";

    // Animation variants
    const variants = {
        pulse: {
            scale: [1, 1.1, 1],
            transition: { repeat: Infinity, duration: 2 }
        },
        bounce: {
            y: [0, -10, 0],
            transition: { repeat: Infinity, duration: 1.5 }
        },
        none: {}
    };

    return (
        <div
            id={id}
            dir="ltr"
            className={cn(
                "fixed bottom-6 z-[100] flex flex-col gap-3",
                positionClasses,
                alignmentClasses,
                isSelected && "ring-4 ring-blue-500/50 rounded-full" // Visual indicator in editor
            )}
            style={{ pointerEvents: 'auto' }} // Ensure clicks work
        >
            <AnimatePresence>
                {showNotification && !isDismissed && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.8 }}
                        className={cn(
                            "bg-white dark:bg-zinc-800 text-foreground shadow-xl rounded-2xl p-4 max-w-[250px] relative border border-border/50",
                            // Origin for scale animation
                            isRight ? "origin-bottom-right" : "origin-bottom-left"
                        )}
                    >
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsDismissed(true);
                                setShowNotification(false);
                            }}
                            className="absolute -top-2 -left-2 bg-muted text-muted-foreground rounded-full p-1 hover:bg-destructive hover:text-white transition-colors border border-border"
                        >
                            <X size={12} />
                        </button>
                        <div className="text-sm font-medium leading-relaxed font-sans" dir="rtl">
                            {notificationMessage}
                        </div>
                        {/* Little triangle arrow */}
                        <div
                            className={cn(
                                "absolute -bottom-2 w-4 h-4 bg-white dark:bg-zinc-800 transform rotate-45 border-b border-r border-border/50",
                                isRight ? "right-6" : "left-6"
                            )}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                onClick={handleClick}
                className="relative group w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95"
                style={{ backgroundColor: finalBgColor }}
                variants={variants}
                animate={animation}
                whileHover={{ scale: 1.1 }}
                drag
                dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }} // Just for fun effect, snaps back
            >
                {/* Ripple Effect Ring */}
                {animation === 'pulse' && (
                    <span
                        className="absolute inset-0 rounded-full animate-ping opacity-30"
                        style={{ backgroundColor: finalBgColor }}
                    />
                )}

                {/* WhatsApp SVG Icon */}
                <svg
                    viewBox="0 0 24 24"
                    className="w-8 h-8 md:w-9 md:h-9 relative z-10"
                    fill={finalIconColor}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.272-.57-.422z" />
                    <path d="M12.001 0C5.378 0 0 5.372 0 11.99c0 2.112.548 4.155 1.59 5.956L.002 24l6.095-1.59A11.96 11.96 0 0012 24c6.623 0 12-5.378 12-11.99C24 5.385 18.627 0 12.001 0zm0 21.815c-1.795 0-3.555-.462-5.12-1.33l-.367-.205-3.804.992 1.014-3.708-.239-.381c-.96-1.534-1.466-3.32-1.466-5.176 0-5.418 4.408-9.826 9.83-9.826 5.422 0 9.83 4.408 9.83 9.826 0 5.418-4.408 9.815-9.68 9.802z" fillRule="evenodd" fillOpacity={finalIconColor === '#ffffff' ? 0.2 : 0} />
                </svg>
            </motion.button>
        </div>
    );
}

// ------------------------------------------------------------------
// Schema
// ------------------------------------------------------------------

export const StickyWhatsAppDefaultProps = {
    phoneNumber: "",
    message: "مرحبًا، أرغب في الاستفسار عن...",
    backgroundColor: "#25D366",
    iconColor: "#ffffff",
    position: "bottom-right",
    animation: "pulse",
    enableNotification: true,
    notificationMessage: "مرحبًا 👋 هل تحتاج إلى مساعدة؟",
    notificationDelay: 5
};

export const StickyWhatsAppSchema: EditorField[] = [
    {
        key: "phoneNumber",
        label: "رقم الوتساب",
        type: "textarea",
        placeholder: "مثال: 0550123456",
        description: "أدخل الرقم بدون مسافات أو رموز"
    },
    {
        key: "message",
        label: "الرسالة الافتراضية",
        type: "textarea",
        defaultValue: "مرحبًا، أرغب في الاستفسار عن..."
    },
    {
        key: "position",
        label: "موقع الأيقونة",
        type: "select",
        options: [
            { label: "أسفل اليمين", value: "bottom-right" },
            { label: "أسفل اليسار", value: "bottom-left" }
        ],
        defaultValue: "bottom-right"
    },
    {
        key: "animation",
        label: "نوع الحركة",
        type: "select",
        options: [
            { label: "نبض (Pulse)", value: "pulse" },
            { label: "قفز (Bounce)", value: "bounce" },
            { label: "بدون حركة", value: "none" }
        ],
        defaultValue: "pulse"
    },
    {
        key: "enableNotification",
        label: "تفعيل الإشعار التلقائي",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "notificationMessage",
        label: "نص الإشعار",
        type: "text",
        defaultValue: "مرحبًا 👋 هل تحتاج إلى مساعدة؟"
    },
    {
        key: "notificationDelay",
        label: "تأخير ظهور الإشعار (ثواني)",
        type: "slider",
        defaultValue: 5,
        sliderConfig: {
            min: 1,
            max: 60,
            step: 1,
            unit: "s"
        }
    },
    {
        key: "backgroundColor",
        label: "لون الخلفية",
        type: "color",
        defaultValue: "#25D366"
    },
    {
        key: "iconColor",
        label: "لون الأيقونة",
        type: "color",
        defaultValue: "#ffffff"
    }
];
